import * as React from "react";

interface GrapeIllustrationProps {
  /** Size in pixels (width). Height auto-scaled by viewBox. Default 150. */
  size?: number;
  /** Show the fallen seed + shadow under the bunch. Default true. */
  tilted?: boolean;
  /** SVG color (defaults to currentColor; parent sets via `style.color`). */
  color?: string;
  className?: string;
}

/**
 * Grozd ilustracija sa palim zrnom — 1:1 iz mockupa 404-i-greske.html.
 *
 * 5 outline zrna (puna), 6. zrno je dashed (palo) — i dolje crveni "fallen-seed"
 * disk + meka sjenka. Koristi se na 404 i Error stranicama.
 *
 * Boje:
 * - SVG koristi `currentColor` → roditelj postavi `color: var(--primary)` ili
 *   inline style; u dark modu se prebacuje na bijelu (vidi NotFoundPage/ErrorPage).
 * - Fallen seed/shadow koriste --primary/--shadow-fallen-glow CSS vars.
 */
export const GrapeIllustration: React.FC<GrapeIllustrationProps> = ({
  size = 150,
  tilted = true,
  color = "currentColor",
  className,
}) => {
  const height = Math.round(size * 1.2);

  return (
    <div
      className={className}
      style={{ position: "relative", display: "inline-block" }}
    >
      <svg
        viewBox="0 0 200 240"
        width={size}
        height={height}
        style={{ color, display: "block" }}
        aria-hidden="true"
      >
        {/* Listić */}
        <g fill="currentColor">
          <path d="M100 68Q114 48 132 40Q124 58 114 66Z" />
        </g>
        <line
          x1="100"
          y1="68"
          x2="118"
          y2="52"
          stroke="currentColor"
          strokeWidth="3"
        />
        {/* 5 outline zrna */}
        <g fill="none" stroke="currentColor" strokeWidth="6">
          <circle cx="60" cy="100" r="22" />
          <circle cx="100" cy="100" r="22" />
          <circle cx="140" cy="100" r="22" />
          <circle cx="80" cy="140" r="22" />
          <circle cx="120" cy="140" r="22" />
        </g>
        {/* 6. zrno — dashed (palo) */}
        <g
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeDasharray="4 4"
          opacity="0.35"
        >
          <circle cx="100" cy="180" r="22" />
        </g>
      </svg>

      {tilted && (
        <>
          {/* Palo zrno */}
          <div
            style={{
              position: "absolute",
              left: "50%",
              top: "calc(100% + 8px)",
              transform: "translateX(-50%) rotate(18deg)",
              width: 40,
              height: 40,
              borderRadius: "50%",
              background: "var(--primary)",
              boxShadow: "var(--shadow-fallen, 0 8px 24px rgba(90,58,181,.35))",
            }}
          />
          {/* Sjenka palog zrna */}
          <div
            style={{
              position: "absolute",
              left: "50%",
              top: "calc(100% + 52px)",
              transform: "translateX(-50%)",
              width: 80,
              height: 8,
              borderRadius: "50%",
              background:
                "radial-gradient(ellipse, var(--shadow-fallen-glow, rgba(90,58,181,.25)), transparent 70%)",
            }}
          />
        </>
      )}
    </div>
  );
};
